import { OnlineSales } from 'gw-edge-client';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../recoil/atoms/opcInfoAtom';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { isOpcFetchingAtom } from '../recoil/atoms/isOpcFetchingAtom';
import { callMeVariantOpcAtom } from '../recoil/atoms/callMeVariantOpcAtom';

const useOpcJob = (): {
    createNewPolicyChange: () => Promise<void>;
    withdrawOPCShoppingBasket: (shoppingBasketId: string, sessionId: string) => Promise<void>;
    withdrawCurrentOPCShoppingBasket: () => Promise<void>;
    createPolicyChangeWithRenewalDateForOPC: () => Promise<void>;
    withdrawCurrentOPCShoppingBasketIfNotExisting: () => Promise<void>;
} => {
    const { policeNummer, coverableId, productName } = useRecoilValue(opcInfoAtom);
    const [jobInfo, setJobInfo] = useRecoilState(jobInfoAtom);
    const { jobNumber, sessionId, isExistingBasket } = jobInfo;
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const setCallMe = useSetRecoilState(callMeVariantOpcAtom);

    const createNewPolicyChange = async (): Promise<void> => {
        await OnlineSales.createPolicyChangeForOPC(policeNummer, coverableId, productName).then(
            (jobInfo): void => {
                setJobInfo(jobInfo);
            }
        );
    };

    const createPolicyChangeWithRenewalDateForOPC = async (): Promise<void> => {
        await OnlineSales.createPolicyChangeWithRenewalDateForOPC(jobNumber, sessionId)
            .then((newJobInfo): void => {
                setJobInfo(newJobInfo);
            })
            .catch((e: Error) => {
                // Open call me if renew failed
                if (e.message.includes('Cannot renew policy due to')) {
                    setCallMe('default');
                }
                throw e;
            });
    };

    const withdrawOPCShoppingBasket = async (
        shoppingBasketId: string,
        sessionId: string
    ): Promise<void> => {
        await OnlineSales.withdrawOPCShoppingBasket(shoppingBasketId, policeNummer, sessionId);
    };

    const withdrawCurrentOPCShoppingBasket = async (): Promise<void> => {
        await withdrawOPCShoppingBasket(jobNumber, sessionId);
    };

    const withdrawCurrentOPCShoppingBasketIfNotExisting = async (): Promise<void> => {
        !isExistingBasket && (await withdrawOPCShoppingBasket(jobNumber, sessionId));
    };

    const withOPCFetching =
        <T, A extends unknown[]>(
            request: (...args: A) => Promise<T>
        ): ((...args: A) => Promise<T>) =>
        async (...args: A) => {
            setIsOpcFetching(true);
            return await request(...args).finally(() => {
                setIsOpcFetching(false);
            });
        };

    return {
        createNewPolicyChange: withOPCFetching(createNewPolicyChange),
        withdrawOPCShoppingBasket: withOPCFetching(withdrawOPCShoppingBasket),
        createPolicyChangeWithRenewalDateForOPC: withOPCFetching(
            createPolicyChangeWithRenewalDateForOPC
        ),
        withdrawCurrentOPCShoppingBasket: withOPCFetching(withdrawCurrentOPCShoppingBasket),
        withdrawCurrentOPCShoppingBasketIfNotExisting: withOPCFetching(
            withdrawCurrentOPCShoppingBasketIfNotExisting
        )
    };
};
export default useOpcJob;
